import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import type { FirebaseError } from 'firebase/app';
import {
  auth,
  createUserWithEmailAndPassword,
  getRedirectResult,
  googleProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from '../lib/firebase';

type AuthContextValue = {
  user: User | null;
  token: string | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
};

const AuthSessionContext = createContext<AuthContextValue | null>(null);

function isPopupBlockedError(error: unknown): boolean {
  const code = (error as FirebaseError | undefined)?.code;
  return code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request';
}

export function AuthSessionProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void getRedirectResult(auth).catch(() => undefined);

    const unsubscribe = onIdTokenChanged(auth, async (nextUser) => {
      if (cancelled) {
        return;
      }

      setUser(nextUser);
      setToken(nextUser ? await nextUser.getIdToken() : null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const signInWithEmailAction = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email.trim(), password);
  }, []);

  const signUpWithEmailAction = useCallback(async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email.trim(), password);
  }, []);

  const signInWithGoogleAction = useCallback(async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      if (isPopupBlockedError(error)) {
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      throw error;
    }
  }, []);

  const signOutUserAction = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      loading,
      signInWithEmail: signInWithEmailAction,
      signUpWithEmail: signUpWithEmailAction,
      signInWithGoogle: signInWithGoogleAction,
      signOutUser: signOutUserAction,
    }),
    [loading, signInWithEmailAction, signInWithGoogleAction, signOutUserAction, signUpWithEmailAction, token, user]
  );

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

export function useAuthSession() {
  const context = useContext(AuthSessionContext);

  if (!context) {
    throw new Error('useAuthSession must be used within AuthSessionProvider');
  }

  return context;
}
